// Import types but not apiRequest (we'll add our own)
import {
  FieldMapping,
  ChoiceMapping,
  SyncModeStatistics,
  CreateFieldMappingRequest,
  CreateChoiceMappingRequest,
  DiscoverMappingsRequest,
  UpdateSyncModeRequest,
  ApiResponse,
  DiscoveryResponse,
  UpdateSyncModeResponse,
  AllMappingsResponse,
  SyncMode,
  SyncLogsResponse,
  EntityMapping,
  CreateEntityMappingRequest,
  UpdateEntityMappingRequest,
  EntityMappingResponse,
  DataverseEntityInfo,
  DataverseFieldInfo,
  FieldPair,
  TaskResponse,
  ProcessEntityRecordsParams,
  BatchProcessResponse,
  TaskLogsResponse,
  QueryTableLogsParams,
  TableLogsQueryResponse
} from "../../../../api/src/lib/models.js";
import { msalInstance } from "./msalInstance";
import { loginRequest } from "./msalConfig";

// Super simple URL builder - just join base URL with /api/ prefix and the path
export function buildApiUrl(path: string): string {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:7071";
  
  console.log(`[API] Building URL with base: ${baseUrl} and path: ${path}`);
  return `${baseUrl}${path}`;
}

// Helper function for making API requests with enhanced debugging and URL handling
export async function apiRequest(
  method: string,
  path: string,
  body?: any
): Promise<Response> {
  // Get auth token directly from MSAL instance - this is more reliable than window.msal
  let token: string | null = null;
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      const result = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]
      });
      token = result.accessToken;
      console.log(`[AUTH] Got token directly from MSAL for ${path}: ${token.substring(0, 10)}...`);
    } else {
      console.warn('[AUTH] No accounts found in MSAL, trying interactive login');
      try {
        const result = await msalInstance.acquireTokenPopup(loginRequest);
        token = result.accessToken;
        console.log('[AUTH] Successfully got token via popup');
      } catch (popupError) {
        console.error('[AUTH] Failed to get token via popup:', popupError);
      }
    }
  } catch (error) {
    console.error(`[AUTH] Error getting token for ${path}:`, error);
  }

  const options: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  // Add auth token if available
  if (token) {
    (options.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    console.log(`[AUTH] Added token to ${path} request`);
  } else {
    console.warn(`[AUTH] No token for ${path} - auth headers will be missing`);
  }

  // Use our simplified URL builder
  const url = buildApiUrl(path);

  try {
    // console.log(`[API] Making ${method} request to: ${url}`);
    // console.log(`[API] Headers:`, options.headers);
    const response = await fetch(url, options);
    
    if (response.status === 401 || response.status === 403) {
      console.warn(`Auth error ${response.status} when calling API: ${url}`);
    }
    
    return response;
  } catch (error) {
    console.error(`API request error for ${url}:`, error);
    throw error;
  }
}

// Field mapping API calls
export const fetchAllMappings = async (): Promise<AllMappingsResponse> => {
  console.log("Client: fetchAllMappings called");
  try {
    // Changed from /api/field-mappings to /api/field-mappings to match queryKey and server endpoint
    const res = await apiRequest("GET", "/api/field-mappings");
    const data = await res.json();
    console.log("Client: fetchAllMappings response:", {
      status: res.status,
      dataStructure: data ? Object.keys(data) : "No data",
      fieldMappingsCount: data?.fieldMappings?.length || 0,
      choiceMappingsCount: data?.choiceMappings?.length || 0,
    });
    return data;
  } catch (error) {
    console.error("Client: Error in fetchAllMappings:", error);
    throw error;
  }
};

export const fetchFieldMappings = async (
  entityName?: string,
  syncMode?: SyncMode,
): Promise<FieldMapping[]> => {
  // Use the combined endpoint with type parameter
  let url = "/api/field-mappings";
  const params = new URLSearchParams();

  // This directs the server to return only field mappings
  params.append("type", "field");
  if (entityName) params.append("entityName", entityName);
  if (syncMode) params.append("syncMode", syncMode);

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  console.log(`Client: Fetching field mappings from: ${url}`);
  const res = await apiRequest("GET", url);
  return res.json();
};

export const createFieldMapping = async (
  mapping: CreateFieldMappingRequest,
): Promise<ApiResponse> => {
  const res = await apiRequest("POST", "/api/field-mappings", mapping);
  return res.json();
};

export const deleteMapping = async (
  partitionKey: string,
  rowKey: string,
): Promise<ApiResponse> => {
  console.log("Client: Starting deleteMapping call with params:", {
    partitionKey,
    rowKey,
  });

  
  const url = `/api/field-mappings?partitionKey=${encodeURIComponent(partitionKey)}&rowKey=${encodeURIComponent(rowKey)}`;

  console.log("Client: Sending DELETE request to:", url);

  try {
    const res = await apiRequest("DELETE", url);
    console.log("Client: Delete response status:", res.status);
    const jsonResponse = await res.json();
    console.log("Client: Delete response data:", jsonResponse);
    return jsonResponse;
  } catch (error) {
    console.error("Client: Error in deleteMapping:", error);
    throw error;
  }
};

// Choice mapping API calls
export const fetchChoiceMappings = async (
  entityName?: string,
  fieldName?: string,
): Promise<ChoiceMapping[]> => {
  // Use the dedicated choice-mappings endpoint
  let url = "/api/field-mappings?partitionKey=ChoiceMapping";
  const params = new URLSearchParams();

  // Add query parameters if they exist
  if (entityName) params.append("entityName", entityName);
  if (fieldName) params.append("fieldName", fieldName);

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  try {
    // Call the dedicated endpoint
    console.log(`Client: Fetching choice mappings from: ${url}`);
    const res = await apiRequest("GET", url);
    const data = await res.json();

    // Handle response based on the structure
    if (Array.isArray(data)) {
      return data.map(mapping => {
        // Start with the original mapping object
        const baseMapping = { ...mapping };
        
        // Ensure required properties have default values if missing
        if (!baseMapping.partitionKey) baseMapping.partitionKey = "";
        if (!baseMapping.rowKey) baseMapping.rowKey = "";
        if (!baseMapping.timestamp) baseMapping.timestamp = new Date().toISOString();
        if (!baseMapping.entityName) baseMapping.entityName = "";
        if (!baseMapping.fieldName) baseMapping.fieldName = "";
        if (!baseMapping.kupValue) baseMapping.kupValue = "";
        if (!baseMapping.hsValue) baseMapping.hsValue = "";
        if (!baseMapping.hsLabel) baseMapping.hsLabel = "";
        if (!baseMapping.etag) baseMapping.etag = "";
        
        return baseMapping as ChoiceMapping;
      });
    } else if (data && Array.isArray(data.choiceMappings)) {
      return data.choiceMappings;
    }
    
    return [];
  } catch (error) {
    console.error("Error fetching choice mappings:", error);
    
    // If there's an error, return an empty array
    return [];
  }
};

export const createChoiceMapping = async (
  mapping: CreateChoiceMappingRequest,
): Promise<ApiResponse> => {
  const res = await apiRequest("POST", "/api/choice-mappings", mapping);
  return res.json();
};

// Statistics API calls
export const fetchSyncModeStats = async (
  entityName: string,
): Promise<SyncModeStatistics> => {
  // Use the dedicated statistics endpoint instead
  const url = `/api/statistics/${encodeURIComponent(entityName)}`;
  console.log("Fetching stats from:", url);
  const res = await apiRequest("GET", url);
  const data = await res.json();
  console.log("Stats API response:", data);
  return data;
};

// Auto-discovery API calls
export const discoverMappings = async (
  request: DiscoverMappingsRequest,
): Promise<DiscoveryResponse> => {
  console.log("Client: Starting discoverMappings with request:", request);
  try {
    // Changed from /api/field-mappings to /api/discover to match server endpoint
    const res = await apiRequest("POST", "/api/discover", request);
    console.log("Client: Auto-discover response status:", res.status);
    const jsonResponse = await res.json();
    console.log("Client: Auto-discover response data:", jsonResponse);
    return jsonResponse;
  } catch (error) {
    console.error("Client: Error in discoverMappings:", error);
    throw error;
  }
};

// Bulk update API calls
export const updateSyncMode = async (
  request: UpdateSyncModeRequest,
): Promise<UpdateSyncModeResponse> => {
  const res = await apiRequest("POST", "/api/update-sync-mode", request);
  return res.json();
};

// Azure Storage and Sync Logs API calls
export const checkAzureStorageStatus = async (): Promise<{
  success: boolean;
  configured: boolean;
  message: string;
}> => {
  const res = await apiRequest("GET", "/api/system/azure-storage-status");
  return res.json();
};

export const fetchSyncLogs = async (
  limit?: number,
): Promise<SyncLogsResponse> => {
  // Use the new queryCustomEvents function
  return await queryCustomEvents({
    eventName: "EntitySync",
    includeStatusCounts: true,
    limit: limit || 100
  });
};

export const fetchEntitySyncLogs = async (
  parentEntity?: string,
  childEntity?: string,
  status?: string,
): Promise<SyncLogsResponse> => {
  // Build query parameters for the new API
  const params: any = {
    eventName: "EntitySync", // Filter for entity sync events only
    includeStatusCounts: true,
    limit: 100
  };
  
  if (parentEntity && parentEntity !== "all") {
    params.parentEntity = parentEntity;
  }
  
  if (childEntity && childEntity !== "all") {
    params.childEntity = childEntity;
  }
  
  if (status && status !== "all") {
    params.syncStatus = status;
  }
  
  // Call the new queryCustomEvents function
  return await queryCustomEvents(params);
};

export const fetchFieldSyncLogs = async (
  entityName?: string,
  status?: string,
  batchId?: string,
  startTime?: string,
  endTime?: string,
  lastSeenTimestamp?: string,
  lastSeenId?: string,
  searchTerm?: string,
  limit: number = 100
): Promise<SyncLogsResponse> => {
  // Build query parameters for the new API
  const params: any = {
    // Include default parameters
    limit,
    includeStatusCounts: true
  };
  
  // Defensive: only allow valid entity names (alphanumeric, underscore, dash)
  const validEntityName =
    entityName &&
    entityName !== "all" &&
    /^[a-zA-Z0-9_\-]+$/.test(entityName)
      ? entityName
      : undefined;
  if (validEntityName) {
    params.parentEntity = validEntityName;
  }
  
  // Status needs to use syncStatus parameter name
  if (status && status !== "all") {
    params.status = status;
  }
  
  // Add batchId parameter if provided
  if (batchId && batchId !== "all") {
    params.batchId = batchId;
  }
  
  // Add time range parameters if provided
  if (startTime) {
    params.startTime = startTime;
  }
  
  if (endTime) {
    params.endTime = endTime;
  }
  
  // Add pagination parameters if provided
  if (lastSeenTimestamp) {
    params.lastSeenTimestamp = lastSeenTimestamp;
  }
  
  if (lastSeenId) {
    params.lastSeenId = lastSeenId;
  }
  // Add server-side search term if provided
  if (searchTerm) {
    params.searchTerm = searchTerm;
  }
   
   console.log("Field Sync Logs - Request params:", {
     entityName, 
     status,
     batchId,
     startTime,
     endTime,
     lastSeenTimestamp,
     lastSeenId,
     limit,
     params
   });
  
  // Call the new queryCustomEvents function
  return await queryCustomEvents(params);
};

// Entity Mapping API calls
export const fetchEntityMappings = async (): Promise<EntityMapping[]> => {
  const res = await apiRequest("GET", "/api/entity-mappings");
  const mappings = (await res.json()) as EntityMapping[];

  // Include EntityMapping name in the response
  return mappings.map((m) => ({
    ...m,
    name: `${m.parentEntity}_${m.childEntity}_${m.parentLookupField}_${m.childLookupField}`,
  }));
};

export const createEntityMapping = async (
  mapping: CreateEntityMappingRequest,
): Promise<EntityMappingResponse> => {
  const res = await apiRequest("POST", "/api/entity-mappings", mapping);
  return res.json();
};

export const updateEntityMapping = async (
  mapping: UpdateEntityMappingRequest,
): Promise<EntityMappingResponse> => {
  const res = await apiRequest("PATCH", "/api/entity-mappings", mapping);
  return res.json();
};

export const deleteEntityMapping = async (
  parentEntity: string,
  childEntity: string,
  parentLookupField: string,
  childLookupField: string
): Promise<EntityMappingResponse> => {
  const url = `/api/entity-mappings?parentEntity=${encodeURIComponent(parentEntity)}&childEntity=${encodeURIComponent(childEntity)}&parentLookupField=${encodeURIComponent(parentLookupField)}&childLookupField=${encodeURIComponent(childLookupField)}`;
  const res = await apiRequest("DELETE", url);
  return res.json();
};

// New Entity Mapping Metadata API calls
export const fetchDataverseEntities = async (): Promise<
  DataverseEntityInfo[]
> => {
  const res = await apiRequest("GET", "/api/entity-mappings/entities");
  return res.json();
};

export const fetchEntityFields = async (
  entityName: string,
  excludeBuiltin?: boolean,
): Promise<DataverseFieldInfo[]> => {
  const params = new URLSearchParams();
  params.append("entityName", entityName);

  if (excludeBuiltin !== undefined) {
    params.append("excludeBuiltin", excludeBuiltin.toString());
  }

  const url = `/api/entity-mappings/fields?${params.toString()}`;
  const res = await apiRequest("GET", url);
  return res.json();
};
// Count Dataverse Records API
export const countDataverseRecords = async (
  entity: string,
  filter: import("../../shared/filter-types").FilterObject
): Promise<number> => {
  // For now, hardcode primaryIdField for pl_title; this should be dynamic for other entities
  const primaryIdField = entity === "pl_title" ? "pl_titleid" : "id";
  const res = await apiRequest("POST", "/api/dataverse/count-records", {
    entityName: entity,
    primaryIdField,
    filter,
  });
  if (!res.ok) {
    const error = await res.text();
    throw new Error(error || "Failed to count records");
  }
  const data = await res.json();
  if (typeof data.count === "number") return data.count;
  throw new Error("Invalid response from countDataverseRecords API");
};

// Add missing function from simplified-api.ts
export const fetchEntityLookupFields = async (
  entityName: string,
  excludeBuiltin?: boolean,
): Promise<DataverseFieldInfo[]> => {
  const params = new URLSearchParams();
  params.append("entityName", entityName);

  if (excludeBuiltin !== undefined) {
    params.append("excludeBuiltin", excludeBuiltin.toString());
  }

  const url = `/api/entity-mappings/lookupFields?${params.toString()}`;
  const res = await apiRequest("GET", url);
  return res.json();
};

// Add missing function from simplified-api.ts
export const autoMatchFields = async (
  parentEntity: string,
  childEntity: string,
  excludeBuiltin?: boolean,
): Promise<FieldPair[]> => {
  const params = new URLSearchParams();
  params.append("parentEntity", parentEntity);
  params.append("childEntity", childEntity);

  if (excludeBuiltin !== undefined) {
    params.append("excludeBuiltin", excludeBuiltin.toString());
  }

  const url = `/api/entity-mappings/auto-match?${params.toString()}`;
  const res = await apiRequest("GET", url);
  return res.json();
};

// Azure Table Logs API - New Centralized API
export const queryTableLogs = async (
  params: import("@shared/api-types").QueryTableLogsParams,
  apiKey?: string,
): Promise<import("@shared/api-types").TableLogsQueryResponse> => {
  if (!params.tableName) {
    throw new Error("tableName is required");
  }

  // Create URL with query parameters
  const queryParams = new URLSearchParams();

  // Add all parameters to the query string
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      // Handle jsonFilters specially - convert object to JSON string
      if (key === "jsonFilters" && typeof value === "object") {
        queryParams.append(key, JSON.stringify(value));
      } else {
        queryParams.append(key, String(value));
      }
    }
  });

  // The function key will be added on the server side
  const url = `/api/table-logs?${queryParams.toString()}`;

  console.log("CLIENT TABLE LOGS REQUEST:", {
    url,
    tableName: params.tableName,
    filters: params.jsonFilters,
    fullUrl: window.location.origin + url,
  });

  try {
    // Use fetch without API key headers - authentication happens server-side
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Client: queryTableLogs - Error response:", errorData);
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`,
      );
    }

    const data = await response.json();
    console.log(
      `Client: queryTableLogs - Retrieved ${data.count} of ${data.totalCount} logs from ${params.tableName}`,
    );
    return data;
  } catch (error) {
    console.error("Client: Error in queryTableLogs:", error);
    throw error;
  }
};

/**
 * Query custom events from Application Insights
 *
 * @param params Query parameters for filtering custom events
 * @returns Promise resolving to query results
 */
export const queryCustomEvents = async (
  params: {
    eventName?: string;
    parentEntity?: string;
    childEntity?: string;
    parentId?: string;
    childId?: string;
    success?: boolean;
    syncStatus?: string;
    batchId?: string;
    mappingId?: string;
    startTime?: string;
    endTime?: string;
    timeRange?: string; // Add timeRange parameter
    limit?: number;
    skip?: number;
    correlationId?: string;
    includeProcessingLog?: boolean;
    includeUpdatedFields?: boolean;
    includeStatusCounts?: boolean;
    lastSeenTimestamp?: string;
    lastSeenId?: string;
    status?: string; // Add status parameter to match fetchFieldSyncLogs
  }
): Promise<SyncLogsResponse> => {
  // Convert params to query string
  const queryParams = new URLSearchParams();

  // Add all parameters to the query string
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, String(value));
    }
  });
  
  const url = `/api/query-field-mapping-logs?${queryParams.toString()}`;

  console.log("CLIENT CUSTOM EVENTS REQUEST:", {
    url,
    params,
  });

  try {
    const response = await apiRequest("GET", url);

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Client: queryCustomEvents - Error response:", errorData);
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();
    console.log(
      `Client: queryCustomEvents - Retrieved ${data.logs.length} of ${data.totalCount} logs`
    );

    // Transform the response to match the expected SyncLogsResponse format
    return {
      logs: data.logs,
      totalCount: data.totalCount,
      statusCounts: data.statusCounts || {
        done: 0,
        error: 0,
        no_update_needed: 0,
        pending: 0,
        total: data.totalCount,
      }
    };
  } catch (error) {
    console.error("Client: Error in queryCustomEvents:", error);
    throw error;
  }
};

// Task API calls
export const executeTask = async (
  taskName: string,
  params?: Record<string, any>,
): Promise<TaskResponse> => {
  const response = await apiRequest("POST", "/api/tasks/execute", {
    taskName,
    params,
  });
  return await response.json();
};

export const processEntityRecords = async (
  params: ProcessEntityRecordsParams,
): Promise<BatchProcessResponse> => {
  const response = await apiRequest(
    "POST",
    "/api/management-tasks/start-process-entity-records",
    params,
  );
  return await response.json();
};

export const fetchTaskLogs = async (filter?: {
  status?: string;
  name?: string;
  limit?: number;
}): Promise<TaskLogsResponse> => {
  const queryParams = new URLSearchParams();
  queryParams.append("tableName", "ManagementTasks");
  if (filter?.status) {
    queryParams.append("status", filter.status);
  }

  if (filter?.name) {
    queryParams.append("name", filter.name);
  }

  if (filter?.limit) {
    queryParams.append("limit", filter.limit.toString());
  }

  const url = `/api/table-logs?${queryParams.toString()}`;
  const response = await apiRequest("GET", url);
  return await response.json();
};

// API Rate Limit Status
export interface RateLimitStatus {
  availableTokens: number;
  maxTokens: number;
  currentRate: number;
  percentAvailable: number;
  timestamp: string;
}

export interface RateLimitResponse {
  success: boolean;
  data?: RateLimitStatus;
  error?: string;
}

/**
 * Fetch the current status of Dataverse API rate limits
 * @returns Promise with rate limit status data
 */
export const getRateLimitStatus = async (): Promise<RateLimitResponse> => {
  try {
    // Use our new apiRequest helper for consistent URL handling
    const response = await apiRequest("GET", "/api/management-tasks/rate-limits");
    return await response.json();
  } catch (error) {
    console.error('Error fetching rate limit status:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error fetching rate limit status'
    };
  }
};



// Start filtered data retrieval job
export async function startFilteredDataRetrieval(entity: string, filter: any) {
  const res = await apiRequest("POST", "/api/dataverse/filtered-retrieval-job", { entity, filter });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || "Failed to start data retrieval job");
  }
  return res.json();
}